import { useState, useMemo } from 'react'
import { Users, X, Shield, Building2, Store, LogIn } from 'lucide-react'
import { clsx } from 'clsx'
import { getAppUrl, getAppPorts } from '../utils/appPorts'

interface RoleOption {
  id: string
  name: string
  description: string
  icon: React.ComponentType<{ className?: string }>
  url: string
  bgColor: string
  textColor: string
}

export function RoleSelector() {
  const [isOpen, setIsOpen] = useState(false)

  // Get current origin and ports
  const protocol = window.location.protocol
  const hostname = window.location.hostname
  const origin = `${protocol}//${hostname}`
  const isLocalhost = hostname === 'localhost' || hostname === '127.0.0.1'
  const ports = getAppPorts()
  const currentPort = window.location.port ? parseInt(window.location.port, 10) : (protocol === 'https:' ? 443 : 80)
  const landingPort = isLocalhost ? (currentPort || ports.landing) : null

  // Dynamically generate roles with URLs based on current port configuration
  const roles: RoleOption[] = useMemo(() => {
    // Helper to get platform admin URL - Platform Admin runs on port 3002
    const getPlatformAdminUrl = (): string => {
      if (isLocalhost) {
        // Prefer gateway if landing is on 3000 (gateway port)
        if (landingPort === 3000) {
          return `${origin}:3000/platform-admin/login`
        }
        // Use direct port access - platform-admin is on 3002
        const platformAdminPort = import.meta.env.VITE_PLATFORM_ADMIN_PORT 
          ? parseInt(import.meta.env.VITE_PLATFORM_ADMIN_PORT, 10) 
          : 3002
        return `${origin}:${platformAdminPort}/login`
      }
      return `${origin}/platform-admin/login`
    }

    // Helper to get tenant admin URL - Tenant Admin runs on port 3003
    const getTenantAdminUrl = (tenantSlug: string): string => {
      if (isLocalhost) {
        // Prefer gateway if landing is on 3000 (gateway port)
        if (landingPort === 3000) {
          return `${origin}:3000/t/${tenantSlug}/admin`
        }
        // Direct access to tenant-admin port - tenant-admin is on 3003
        const adminPort = import.meta.env.VITE_ADMIN_PORT 
          ? parseInt(import.meta.env.VITE_ADMIN_PORT, 10) 
          : 3003
        return `${origin}:${adminPort}/t/${tenantSlug}/admin`
      }
      return `${origin}/t/${tenantSlug}/admin`
    }

    // Helper to get storefront URL - Storefront runs on port 3004
    const getStorefrontUrl = (tenantSlug: string): string => {
      if (isLocalhost) {
        // Prefer gateway if landing is on 3000 (gateway port)
        if (landingPort === 3000) {
          return `${origin}:3000/t/${tenantSlug}`
        }
        // Direct access to storefront port - storefront is on 3004
        const customerPort = import.meta.env.VITE_CUSTOMER_PORT 
          ? parseInt(import.meta.env.VITE_CUSTOMER_PORT, 10) 
          : 3004
        return `${origin}:${customerPort}/t/${tenantSlug}`
      }
      return `${origin}/t/${tenantSlug}`
    }

    return [
      {
        id: 'platform-admin',
        name: 'Platform Admin',
        description: 'Manage tenants, plans & billing',
        icon: Shield,
        url: getPlatformAdminUrl(),
        bgColor: 'bg-purple-500/10',
        textColor: 'text-purple-600'
      },
      {
        id: 'tenant-admin',
        name: 'Tenant Admin',
        description: 'Cake&Bake admin dashboard',
        icon: Building2,
        url: getTenantAdminUrl('bakers'),
        bgColor: 'bg-primary-600/10',
        textColor: 'text-primary-600'
      },
      {
        id: 'service-specific-cakebake',
        name: 'Cake&Bake Store',
        description: 'Bakery business - Customer store',
        icon: Store,
        url: getStorefrontUrl('bakers'),
        bgColor: 'bg-accent-400/10',
        textColor: 'text-accent-400'
      },
      {
        id: 'service-specific-washpure',
        name: 'WashPure Store',
        description: 'Laundry service demo',
        icon: Store,
        url: getStorefrontUrl('laundry-royal'),
        bgColor: 'bg-info-500/10',
        textColor: 'text-info-500'
      },
      {
        id: 'service-specific-aquapure',
        name: 'Aquapure Store',
        description: 'Water bottling & distribution',
        icon: Store,
        url: getStorefrontUrl('aquapure'),
        bgColor: 'bg-success-500/10',
        textColor: 'text-success-500'
      },
      {
        id: 'login',
        name: 'Login Page',
        description: 'Sign in to your account',
        icon: LogIn,
        url: isLocalhost && landingPort
          ? `${origin}:${landingPort}/login`
          : '/login',
        bgColor: 'bg-neutral-600/10',
        textColor: 'text-neutral-600'
      }
    ]
  }, [origin, isLocalhost, landingPort])

  const handleRoleClick = (role: RoleOption) => {
    // Open all links in new tab
    window.open(role.url, '_blank')
    setIsOpen(false)
  }

  return (
    <div className="fixed bottom-6 right-6 z-50">
      {/* Floating Action Button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className={clsx(
          'w-14 h-14 rounded-full shadow-lg flex items-center justify-center transition-all duration-300',
          isOpen
            ? 'bg-danger-500 hover:bg-danger-600 text-white'
            : 'bg-primary-600 hover:bg-primary-700 text-white'
        )}
        aria-label="Open role selector"
      >
        {isOpen ? (
          <X className="w-6 h-6" />
        ) : (
          <Users className="w-6 h-6" />
        )}
      </button>

      {/* Role Options Menu */}
      {isOpen && (
        <>
          {/* Backdrop */}
          <div
            className="fixed inset-0 bg-black/20 backdrop-blur-sm"
            onClick={() => setIsOpen(false)}
          />
          
          {/* Menu */}
          <div className="absolute bottom-20 right-0 w-72 bg-white dark:bg-brand-900 rounded-xl shadow-2xl border border-neutral-200 dark:border-white/10 overflow-hidden">
            <div className="p-4 border-b border-neutral-200 dark:border-white/10">
              <h3 className="text-lg font-semibold text-neutral-900 dark:text-white">
                Quick Access
              </h3>
              <p className="text-sm text-neutral-600 dark:text-neutral-400 mt-1">
                Select a role to access
              </p>
            </div>
            
            <div className="p-2">
              {roles.map((role) => {
                const Icon = role.icon
                return (
                  <button
                    key={role.id}
                    onClick={() => handleRoleClick(role)}
                    className={clsx(
                      'w-full flex items-start gap-3 p-3 rounded-lg transition-colors text-left',
                      'hover:bg-neutral-100 dark:hover:bg-white/5',
                      'focus:outline-none focus:ring-2 focus:ring-primary-600'
                    )}
                  >
                    <div className={clsx(
                      'w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0',
                      role.bgColor
                    )}>
                      <Icon className={clsx('w-5 h-5', role.textColor)} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium text-neutral-900 dark:text-white">
                        {role.name}
                      </div>
                      <div className="text-xs text-neutral-600 dark:text-neutral-400 mt-0.5">
                        {role.description}
                      </div>
                    </div>
                  </button>
                )
              })}
            </div>
            
            <div className="p-3 border-t border-neutral-200 dark:border-white/10 bg-neutral-50 dark:bg-white/5">
              <p className="text-xs text-neutral-500 dark:text-neutral-400 text-center">
                Use these links for demo/testing purposes
              </p>
            </div>
          </div>
        </>
      )}
    </div>
  )
}

